import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppTableHeaderCell from "../../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../../views/components/common/pages/AppTableRowCell";
import TableState from "../../../../../../views/components/common/table-states/TableState";
import { OpsSyncProjectProductProgress } from "../../../../../../models/ProjectManagement/OpssyncProjectProductProgress";
import { ModuleTasks } from "../../../../../../models/ModuleType";
import { formatDate } from "../../../../../../views/components/common/functions/formatDate";
import { ProductSourcingInfo } from "../../../../../../models/ProjectManagement/ProjectManagementBasicDetails";

interface OpsSyncProductTableProps {
  data: OpsSyncProjectProductProgress[] | [];
  taskTypes: ModuleTasks[] | [];
  maxHeight?: number;
  minHeight?: number;
}

const OpsSyncProductTable = ({
  data,
  taskTypes,
  maxHeight,
  minHeight = window.innerHeight - 500,
}: OpsSyncProductTableProps) => {
  const theme = useTheme();

  const getTabIndexForTask = (taskLabel: string): number | null => {
    const taskMappings: { [key: string]: number } = {
      "Mockups Upload": 1,
      "Mockups Approved": 1,
      "CDR to Printers": 1,
      "PR Created": 3,
      "PO Created": 3,
      "Received in Warehouse": 2,
      "1st Quality Check": 2,
      "Out for Printing": 2,
      "2nd Quality Check": 2,
    };

    return taskMappings[taskLabel] || null;
  };

  const handleTaskClick = (
    orderId: number,
    productId: number,
    poId: number | null,
    taskLabel: string
  ) => {
    const tabIndex = getTabIndexForTask(taskLabel);
    if (tabIndex === 1) {
      window.open(
        `/projects/sourcing-ops-details?project-id=${orderId}&product-id=${productId}`,
        "_blank"
      );
    } else if (tabIndex === 2) {
      window.open(
        `/projects/inventory-ops-details?project-id=${orderId}&product-id=${productId}`,
        "_blank"
      );
    }
    if (tabIndex === 3) {
      window.open(`/projects/${orderId}?tab_index=1`, "_blank");
    }
  };

  const isTaskClickable = (taskLabel: string): boolean => {
    return getTabIndexForTask(taskLabel) !== null;
  };

  return (
    <>
      {!data || data?.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "fit-content",
            textAlign: "center",
            maxHeight: maxHeight,
            minHeight: minHeight,
          }}
        >
          <CustomTypography
            variant="body2"
            fontWeight={700}
            color={theme?.palette?.customColors?.greyTextColor}
          >
            NO PRODUCTS FOUND!
          </CustomTypography>
        </Box>
      ) : (
        <TableContainer
          sx={{
            borderRadius: "4px",
            scrollbarWidth: "none",
            maxHeight: maxHeight,
            minHeight: minHeight,
          }}
        >
          <Table sx={{ tableLayout: "auto", width: "100%" }}>
            <TableHead>
              <TableRow
                sx={{
                  marginBottom: "10px",
                  backgroundColor:
                    theme.palette.customColors.grayBackGroundColor,
                  "& th": { borderBottom: "none" },
                }}
              >
                <AppTableHeaderCell
                  minWidth="0.75rem"
                  fontSize="0.75rem"
                  fontWeight={700}
                  fontColor={theme?.palette?.customColors?.blackBodyBackgorund}
                  sx={{
                    paddingLeft: 2,
                    whiteSpace: "pre-line",
                    textAlign: "left",
                    lineHeight: "1.2rem",
                    paddingTop: "0.6rem !important",
                    paddingBottom: "0.6rem !important",
                  }}
                >
                  Product{"\n"}Name
                </AppTableHeaderCell>

                <AppTableHeaderCell
                  minWidth="200px"
                  fontSize="0.75rem"
                  fontWeight={700}
                  fontColor={theme?.palette?.customColors?.blackBodyBackgorund}
                  sx={{
                    paddingLeft: 1,
                    whiteSpace: "pre-line",
                    textAlign: "center",
                    lineHeight: "1.2rem",
                    paddingTop: "0.6rem !important",
                    paddingBottom: "0.6rem !important",
                  }}
                >
                  Customization
                  {"\n"}Required
                </AppTableHeaderCell>

                {taskTypes?.map((item: ModuleTasks, index: number) => (
                  <AppTableHeaderCell
                    key={index}
                    minWidth="100px"
                    fontSize="0.75rem"
                    fontWeight={700}
                    fontColor={
                      theme?.palette?.customColors?.blackBodyBackgorund
                    }
                    sx={{
                      paddingTop: "0.6rem !important",
                      paddingBottom: "0.6rem !important",
                    }}
                  >
                    <Box sx={{ whiteSpace: "pre-line", lineHeight: "1.2rem" }}>
                      {item?.ops_sync_label}
                    </Box>
                  </AppTableHeaderCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {data?.map(
                (item: OpsSyncProjectProductProgress, index: number) => (
                  <TableRow key={index}>
                    <AppTableRowCell
                      sx={{
                        padding: 1,
                        flexWrap: "wrap",
                        borderBottom: "none !important",
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <Box
                          sx={{
                            background:
                              theme?.palette?.customColors?.grayBackGroundColor,
                            padding: 1,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            marginRight: 2,
                            minHeight: "55px",
                            maxHeight: "55px",
                            borderRadius: "4px",
                          }}
                        >
                          <img
                            style={{
                              maxHeight: "38px",
                              minHeight: "38px",
                              maxWidth: "38px",
                              minWidth: "38px",
                            }}
                            src={
                              item?.image_url ||
                              "https://via.placeholder.com/38"
                            }
                            alt={item?.name}
                          />
                        </Box>
                        <Box sx={{ textAlign: "left" }}>
                          <CustomTypography
                            variant="body2"
                            fontWeight={700}
                            title={item?.name}
                            sx={{ cursor: "default" }}
                          >
                            {item?.name}
                          </CustomTypography>
                          <CustomTypography
                            variant="body3"
                            style={{ paddingTop: 5 }}
                            fontWeight={500}
                            color="rgba(111, 111, 111, 1)"
                          >
                            Quantity: {item?.quantity || "N/A"}
                          </CustomTypography>
                        </Box>
                      </Box>
                    </AppTableRowCell>

                    <AppTableRowCell
                      sx={{
                        textAlign: "center",
                        position: "relative",
                        padding: 0,
                        borderBottom: "none !important",
                        width: "12%",
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        position="relative"
                        width="100%"
                        gap="10px"
                      >
                        <Box
                          sx={{
                            zIndex: 1,
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            backgroundColor:
                              theme?.palette?.customColors?.grayBackGroundColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <TableState state={!!item?.customisation_required} />
                        </Box>
                        <CustomTypography
                          variant="body3"
                          fontWeight={500}
                          sx={{
                            minHeight: "16px",
                            textAlign: "center",
                            lineHeight: "1.2",
                          }}
                        >
                          {item?.customisation_required
                            ? "Required"
                            : "Not Required"}
                        </CustomTypography>
                        <Box
                          sx={{
                            position: "absolute",
                            width: "50%",
                            height: "2px",
                            backgroundColor:
                              theme?.palette?.customColors?.grayBackGroundColor,
                            right: 0,
                            top: "25%",
                            transform: "translateY(-50%)",
                            zIndex: 0,
                          }}
                        />
                      </Box>
                    </AppTableRowCell>

                    {taskTypes?.map(
                      (
                        taskType: ModuleTasks,
                        idx: number,
                        arr: ModuleTasks[]
                      ) => {
                        const completedTask = item?.completed_tasks?.find(
                          (task) => task.task === taskType.task
                        );

                        const isClickable = isTaskClickable(
                          taskType.ops_sync_label || ""
                        );

                        // Get quantity based on task label and sourcing info
                        const getQuantityForTask = (
                          taskLabel?: string,
                          sourcingInfo?: ProductSourcingInfo,
                          taskType?: string
                        ): number | null => {
                          if (!taskLabel || !sourcingInfo) return null;

                          const taskQuantityMap: { [key: string]: string } = {
                            "PR Created": "pr_quantity",
                            "PO Created": "po_quantity",
                            "Received in Warehouse":
                              taskType === "print_inward"
                                ? "pi_total_received_quantity"
                                : "ri_total_received_quantity",
                            "1st Quality Check": "ri_total_rejected_quantity",
                            "Out for Printing": "po_total_received_sent",
                            "Print Inward": "pi_total_received_quantity",
                            "2nd Quality Check": "pi_total_rejected_quantity",
                          };

                          const fieldName = taskQuantityMap[taskLabel];
                          if (
                            fieldName &&
                            sourcingInfo[fieldName as keyof ProductSourcingInfo]
                          ) {
                            const value =
                              sourcingInfo[
                                fieldName as keyof ProductSourcingInfo
                              ];
                            return typeof value === "string"
                              ? parseInt(value)
                              : value;
                          }
                          return null;
                        };

                        const taskQuantity = getQuantityForTask(
                          taskType.ops_sync_label,
                          item?.product_sourcing_status_info,
                          taskType.task
                        );

                        return (
                          <AppTableRowCell
                            key={idx}
                            sx={{
                              textAlign: "center",
                              position: "relative",
                              padding: 0,
                              borderBottom: "none !important",
                              minWidth: "140px",
                              maxWidth: "140px",
                            }}
                          >
                            <Box
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              justifyContent="flex-start"
                              position="relative"
                              width="100%"
                              height="100%"
                              gap="10px"
                            >
                              <Box
                                display="flex"
                                flexDirection="column"
                                sx={{
                                  position: "absolute",
                                  width: "50%",
                                  height: "2px",
                                  backgroundColor:
                                    theme?.palette?.customColors
                                      ?.grayBackGroundColor,
                                  left: 0,
                                  top: "25%",
                                  transform: "translateY(-50%)",
                                  zIndex: 0,
                                }}
                              />
                              <Box
                                sx={{
                                  zIndex: 1,
                                  width: "20px",
                                  height: "20px",
                                  borderRadius: "50%",
                                  backgroundColor:
                                    theme?.palette?.customColors
                                      ?.grayBackGroundColor,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: isClickable ? "pointer" : "default",
                                  transition: "all 0.2s ease-in-out",
                                  marginTop: "auto",
                                  marginBottom: "auto",
                                  ...(isClickable && {
                                    "&:hover": {
                                      transform: "scale(1.1)",
                                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                    },
                                  }),
                                }}
                                onClick={() => {
                                  if (isClickable) {
                                    handleTaskClick(
                                      item.order_id,
                                      item.id,
                                      null,
                                      taskType.ops_sync_label || ""
                                    );
                                  }
                                }}
                              >
                                <TableState
                                  state={completedTask?.active === 1}
                                />
                              </Box>

                              <CustomTypography
                                variant="body3"
                                fontWeight={500}
                                sx={{
                                  minHeight: "16px",
                                  textAlign: "center",
                                  lineHeight: "1.2",
                                  fontSize: "11px",
                                  wordWrap: "break-word",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "100%",
                                  padding: "0 4px",
                                }}
                              >
                                {completedTask?.modified_date
                                  ? formatDate(completedTask?.modified_date)
                                  : ""}
                              </CustomTypography>

                              {taskQuantity !== null && taskQuantity > 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    fontSize: "11px",
                                    fontWeight: 500,
                                    textAlign: "center",
                                    lineHeight: 1,
                                    marginTop: "2px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {taskQuantity}
                                </div>
                              )}

                              {idx < arr?.length - 1 && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    width: "50%",
                                    height: "2px",
                                    backgroundColor:
                                      theme?.palette?.customColors
                                        ?.grayBackGroundColor,
                                    right: 0,
                                    top: "25%",
                                    transform: "translateY(-50%)",
                                    zIndex: 0,
                                  }}
                                />
                              )}
                            </Box>
                          </AppTableRowCell>
                        );
                      }
                    )}
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default OpsSyncProductTable;
